import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { Button, Checkbox, IconButton, MenuItem, Stack, TextField } from "@mui/material";
import { Add, Block, CheckCircle, Edit, Storefront, Sync } from "@mui/icons-material";
import { FilterModal } from "@/components/FilterModal";
import { DataField } from "@/components/DataField";
import usePagination from "@/hooks/pagination";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useNotification } from "@/context/NotificationContext";
import { useCrud } from "@/hooks/useCrud";
import { DataTable } from "@/components/DataTable";
import { CrudModal } from "@/components/crud/CrudModal";
import { CrudToolbar } from "@/components/crud/CrudToolbar";
import {
  ImportadorPlanilha,
  type ColunaImportacao,
  type LinhaPlanilha,
  type ResultadoImportacao,
} from "@/components/import/ImportadorPlanilha";
import { useLogger } from "@/hooks/useLogger";
import { useListaRemota } from "@/hooks/useListaRemota";
import { useOpcoesCadastro } from "@/hooks/useOpcoesCadastro";
import { LogEventType } from "@/api/log.service";
import { useAuth } from "@/context/AuthContext";
import { useAdministradorEscala } from "@/hooks/useAdministradorEscala";
import {
  formatDataBR,
  importarFeriados,
  listFeriados,
  listLojas,
  saveFeriado,
  saveLojasFeriado,
  type EstadoFeriadoLoja,
  type Feriado,
  type FeriadoLoja,
  type Loja,
} from "@/api/escala.service";
import { mensagemDoReflexoDeFeriado } from "@/components/escala/cargaHoraria";
import { EscalaFeriadoLojasModal } from "@/components/escala/EscalaFeriadoLojasModal";
import { resumoLojasDoFeriado } from "@/components/escala/feriadoLojas";

// Colunas do modelo de planilha de importação de feriados — o que o feriado é
// em cada loja não entra na planilha: todo importado abre todas as lojas (ou,
// importado por quem enxerga só algumas, só elas), como o criado na tela, e as
// exceções são marcadas depois, em "Lojas".
const colunasImportacaoFeriados: ColunaImportacao[] = [
  { chave: "data", rotulo: "Data (DD/MM/AAAA)", rotulosAlternativos: ["Data (AAAA-MM-DD)"], tipo: "data", obrigatoria: true, exemplo: "01/01/2026" },
  { chave: "nome", rotulo: "Nome do Feriado", obrigatoria: true, exemplo: "Confraternização Universal" },
];

function resolverFeriadosParaImportar(linhas: LinhaPlanilha[]): Feriado[] {
  return linhas.map((linha) => ({
    data: linha.data ?? "",
    nome: linha.nome ?? "",
    ativo: true,
  }));
}

interface FeriadoFormProps {
  values: Partial<Feriado>;
  setValues: Dispatch<SetStateAction<Partial<Feriado>>>;
  isEditing: boolean;
  // Administrador de Escala: escolhe o padrão de todas as lojas. Os demais criam só para as suas.
  escopoTotal: boolean;
}

// O padrão das lojas só se escolhe na criação; depois, ele e as lojas mudam em "Lojas". "Não é
// feriado" é o municipal: nasce sem valer em loja nenhuma, e as lojas da cidade são marcadas em seguida.
// Quem enxerga só algumas lojas cria o feriado só nelas, fechando ou abrindo; cada uma se ajusta depois.
function FeriadoForm({ values, setValues, isEditing, escopoTotal }: FeriadoFormProps) {
  return (
    <>
      <DataField
        label="Data"
        fullWidth
        required
        value={values.data ?? ""}
        onChange={(data) => setValues((v) => ({ ...v, data }))}
      />
      <TextField
        label="Nome do Feriado"
        fullWidth
        required
        value={values.nome ?? ""}
        onChange={(e) => setValues((v) => ({ ...v, nome: e.target.value }))}
        slotProps={{ htmlInput: { maxLength: 120 } }}
      />
      {!isEditing && !escopoTotal && (
        <TextField
          select
          label="Nas minhas lojas"
          fullWidth
          value={values.padrao === "FECHA" ? "FECHA" : "ABRE"}
          onChange={(e) => setValues((v) => ({ ...v, padrao: e.target.value as EstadoFeriadoLoja }))}
          helperText="Vale só nas lojas a que você tem acesso; cada uma se ajusta depois, em Lojas."
        >
          <MenuItem value="ABRE">Todas abrem</MenuItem>
          <MenuItem value="FECHA">Todas fecham</MenuItem>
        </TextField>
      )}
      {!isEditing && escopoTotal && (
        <TextField
          select
          label="Nas lojas"
          fullWidth
          value={values.padrao ?? "ABRE"}
          onChange={(e) => setValues((v) => ({ ...v, padrao: e.target.value as EstadoFeriadoLoja }))}
          helperText={
            (values.padrao ?? "ABRE") === "NAO_E_FERIADO"
              ? "Feriado municipal: marque as lojas onde ele vale em seguida."
              : "Vale para todas as lojas; as exceções são marcadas depois, em Lojas."
          }
        >
          <MenuItem value="ABRE">Todas abrem</MenuItem>
          <MenuItem value="FECHA">Todas fecham</MenuItem>
          <MenuItem value="NAO_E_FERIADO">Só nas lojas que eu marcar</MenuItem>
        </TextField>
      )}
    </>
  );
}

export function EscalaFeriadosTab() {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const { notify } = useNotification();
  const { log } = useLogger();
  const { total, setTotal, page, setPage, rowsPerPage, setRowsPerPage } = usePagination();
  const { open, editing, handleCreate, handleEdit, handleClose } = useCrud<Feriado>();
  const { administrador } = useAdministradorEscala();

  const anoAtual = new Date().getFullYear();
  const [inputValues, setInputValues] = useState({ ano: String(anoAtual), nome: "" });
  const [filtros, setFiltros] = useState({ ano: anoAtual, nome: "" });

  const [toggleActiveItem, setToggleActiveItem] = useState<Feriado | null>(null);
  const [lojasDoFeriado, setLojasDoFeriado] = useState<Feriado | null>(null);

  const { itens: feriados, carregouAlgumaVez, recarregar } = useListaRemota(
    () => listFeriados(filtros.ano, clientId),
    [clientId, filtros.ano],
    "Erro ao carregar feriados",
  );

  const { opcoes } = useOpcoesCadastro<{ lojas: Loja[] }>(
    { lojas: { buscar: () => listLojas(clientId), mensagemErro: "Erro ao carregar lojas" } },
    [clientId],
  );

  const filtered = useMemo(() => {
    return [...feriados]
      .sort((a, b) => a.data.localeCompare(b.data))
      .filter((f) => !filtros.nome || f.nome.toLowerCase().includes(filtros.nome.toLowerCase()));
  }, [feriados, filtros.nome]);

  useEffect(() => {
    setTotal(filtered.length);
  }, [filtered, setTotal]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const applyFilters = () => {
    const ano = Number(inputValues.ano) || anoAtual;
    setFiltros({ ano, nome: inputValues.nome });
    setPage(0);
  };

  const handleClearFilters = () => {
    setInputValues({ ano: String(anoAtual), nome: "" });
    setFiltros({ ano: anoAtual, nome: "" });
    setPage(0);
  };

  const activeFilterCount = filtros.nome ? 1 : 0;

  const onSave = async (values: Partial<Feriado>) => {
    if (!values.data) {
      notify("Data é obrigatória", "error");
      return;
    }
    if (!values.nome?.trim()) {
      notify("Nome do feriado é obrigatório", "error");
      return;
    }
    // Um feriado por data — o que muda por loja vai nas marcações dele (R49). O de outras lojas que
    // o usuário não enxerga não está na lista: o servidor junta as lojas dele nesse.
    const duplicado = feriados.some((f) => f.data === values.data && f.id !== editing?.id);
    if (duplicado) {
      notify(`Já existe um feriado cadastrado em ${formatDataBR(values.data)} — para mudar as lojas dele, use Lojas`, "error");
      return;
    }

    try {
      const result = await saveFeriado(
        editing
          ? { id: editing.id, data: values.data, nome: values.nome, ativo: editing.ativo }
          : {
              data: values.data,
              nome: values.nome,
              ativo: true,
              // Só o Administrador escolhe "não é feriado"; os demais, só fecha ou abre.
              padrao: administrador ? (values.padrao ?? "ABRE") : values.padrao === "FECHA" ? "FECHA" : "ABRE",
            },
        clientId,
      );

      log(editing ? LogEventType.Updated : LogEventType.Created, result, "escala_feriado", result.id, "info");
      // O horário dos rascunhos com carga acompanha o feriado; a loja fechada com gente escalada só é avisada.
      const base = result.ja_existia
        ? `Já havia o feriado "${result.nome}" em ${formatDataBR(result.data)}; suas lojas foram marcadas nele.`
        : `Feriado ${editing ? "atualizado" : "criado"}!`;
      const aviso = mensagemDoReflexoDeFeriado(base, result);
      notify(aviso.texto, aviso.tipo);
      recarregar();
      handleClose();
      // O municipal do Administrador nasce sem valer em loja nenhuma: já abre a marcação das lojas.
      if (!editing && result.padrao === "NAO_E_FERIADO" && !result.lojas?.length) setLojasDoFeriado(result);
    } catch (e: any) {
      notify(e?.message || "Erro ao salvar feriado", "error");
    }
  };

  const handleImportarFeriados = async (linhasPlanilha: LinhaPlanilha[]): Promise<ResultadoImportacao> => {
    const resultado = await importarFeriados(resolverFeriadosParaImportar(linhasPlanilha), clientId);
    return {
      ...resultado,
      mensagem: mensagemDoReflexoDeFeriado(`${resultado.importados} registro(s) importado(s) com sucesso.`, resultado),
    };
  };

  const onSalvarLojas = async (padrao: EstadoFeriadoLoja, lojas: FeriadoLoja[]) => {
    if (!lojasDoFeriado?.id) return;
    try {
      const result = await saveLojasFeriado(lojasDoFeriado.id, padrao, lojas, clientId);
      log(LogEventType.Updated, result, "escala_feriado", lojasDoFeriado.id, "info");
      const aviso = mensagemDoReflexoDeFeriado("Lojas do feriado atualizadas!", result);
      notify(aviso.texto, aviso.tipo);
      setLojasDoFeriado(null);
      recarregar();
    } catch (e: any) {
      notify(e?.message || "Erro ao salvar as lojas do feriado", "error");
    }
  };

  const onToggleActiveConfirm = async () => {
    if (!toggleActiveItem) return;
    try {
      const ativo = !toggleActiveItem.ativo;
      const result = await saveFeriado({ ...toggleActiveItem, ativo }, clientId);
      log(ativo ? LogEventType.Activated : LogEventType.Deactivated, result, "escala_feriado", toggleActiveItem.id, "info");
      const aviso = mensagemDoReflexoDeFeriado(ativo ? "Feriado ativado!" : "Feriado inativado!", result);
      notify(aviso.texto, aviso.tipo);
      recarregar();
    } catch (e: any) {
      notify(e?.message || "Erro ao atualizar status", "error");
    } finally {
      setToggleActiveItem(null);
    }
  };

  if (!carregouAlgumaVez) return <Sync />;

  return (
    <>
      <CrudToolbar>
        <FilterModal activeCount={activeFilterCount} onApply={applyFilters} onClear={handleClearFilters}>
          <TextField
            label="Ano"
            type="number"
            size="small"
            fullWidth
            value={inputValues.ano}
            onChange={(e) => setInputValues((v) => ({ ...v, ano: e.target.value }))}
          />
          <TextField
            label="Nome"
            size="small"
            fullWidth
            value={inputValues.nome}
            onChange={(e) => setInputValues((v) => ({ ...v, nome: e.target.value }))}
          />
        </FilterModal>
        <Stack direction="row" spacing={2}>
          <ImportadorPlanilha
            titulo="Importar feriados"
            nomeArquivoModelo="modelo_feriados.xlsx"
            colunas={colunasImportacaoFeriados}
            onImportar={handleImportarFeriados}
            onConcluido={recarregar}
          />
          <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
            Adicionar feriado
          </Button>
        </Stack>
      </CrudToolbar>

      <DataTable
        data={paged}
        columns={[
          { key: "ativo", label: "Ativo", render: (f) => <Checkbox checked={f.ativo} disabled /> },
          { key: "data", label: "Data", render: (f) => formatDataBR(f.data) },
          { key: "nome", label: "Nome do Feriado" },
          { key: "padrao", label: "Lojas", render: (f) => resumoLojasDoFeriado(f, opcoes.lojas) },
        ]}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        actions={(feriado) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <IconButton aria-label={`Lojas de ${feriado.nome}`} onClick={() => setLojasDoFeriado(feriado)}>
              <Storefront />
            </IconButton>
            {/* Do feriado que vale também em lojas que o usuário não enxerga, só as lojas dele mudam. */}
            {!feriado.outras_lojas && (
              <>
                <IconButton aria-label={`Editar ${feriado.nome}`} onClick={() => handleEdit(feriado)}>
                  <Edit />
                </IconButton>
                <IconButton
                  aria-label={`${feriado.ativo ? "Inativar" : "Ativar"} ${feriado.nome}`}
                  onClick={() => setToggleActiveItem(feriado)}
                  color={feriado.ativo ? "warning" : "success"}
                >
                  {feriado.ativo ? <Block /> : <CheckCircle />}
                </IconButton>
              </>
            )}
          </Stack>
        )}
      />

      <CrudModal<Feriado, { escopoTotal: boolean }>
        open={open}
        onClose={handleClose}
        title={editing ? "Editar feriado" : "Adicionar feriado"}
        initialData={editing}
        onSubmit={onSave}
        Form={FeriadoForm}
        extraFormProps={{ escopoTotal: administrador }}
      />

      <EscalaFeriadoLojasModal
        feriado={lojasDoFeriado}
        lojas={opcoes.lojas}
        escopoTotal={administrador}
        onClose={() => setLojasDoFeriado(null)}
        onSubmit={onSalvarLojas}
      />

      <ConfirmModal
        open={!!toggleActiveItem}
        onClose={() => setToggleActiveItem(null)}
        onConfirm={onToggleActiveConfirm}
        title={toggleActiveItem?.ativo ? "Inativação" : "Ativação"}
        btnText="Confirmar"
        text={`Deseja ${toggleActiveItem?.ativo ? "inativar" : "ativar"} o feriado ${toggleActiveItem?.nome}?`}
      />
    </>
  );
}
